import { ChangeEvent, useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { Content } from 'src/contexts/ContentContext';
import { PosCurrencyField } from 'src/core/POS/PosCurrencyField';
import { vars } from 'src/core/themes';
import { PriceCeilingIcon } from 'src/svgs/PriceCeilingIcon';
import { PriceFloorIcon } from 'src/svgs/PriceFloorIcon';
import { ContentId } from 'src/utils/constants/contentId';
import {
  getAllInPriceFromListPrice,
  getListPriceFromAllinPrice,
} from 'src/utils/inventoryUtils';
import { UiCurrency } from 'src/WebApiController';

import * as styles from './PricingSettingAutoPriceFloorCeilingInput.css';

type PricingSettingAutoPriceFloorCeilingInputFormFields = {
  netProceedsFloor: number | null;
  netProceedsCeiling: number | null;
};

export const PricingSettingAutoPriceFloorCeilingInput = ({
  uiCurrency,
  sellerFee,
  disabled,
}: {
  uiCurrency: UiCurrency;
  sellerFee: number | null;
  disabled: boolean;
}) => {
  const { setValue, watch } =
    useFormContext<PricingSettingAutoPriceFloorCeilingInputFormFields>();

  const { netProceedsFloor, netProceedsCeiling } = watch();

  return (
    <div className={styles.floorCeilingInputContainer}>
      <div>
        <FloorOrCeilingPosCurrencyField
          uiCurrency={uiCurrency}
          sellerFee={sellerFee}
          icon={<PriceFloorIcon size={vars.iconSize.s} />}
          labelContentId={ContentId.Floor}
          value={netProceedsFloor}
          setValue={(value) => setValue('netProceedsFloor', value)}
          disabled={disabled}
        />
      </div>
      <div>
        <FloorOrCeilingPosCurrencyField
          uiCurrency={uiCurrency}
          sellerFee={sellerFee}
          icon={<PriceCeilingIcon size={vars.iconSize.s} />}
          labelContentId={ContentId.Ceiling}
          value={netProceedsCeiling}
          setValue={(value) => setValue('netProceedsCeiling', value)}
          disabled={disabled}
        />
      </div>
    </div>
  );
};

const FloorOrCeilingPosCurrencyField = ({
  uiCurrency,
  sellerFee,
  icon,
  labelContentId,
  value,
  setValue,
  disabled,
}: {
  uiCurrency: UiCurrency;
  sellerFee: number | null;
  icon: React.ReactNode;
  labelContentId: ContentId;
  value: number | null;
  setValue: (value: number | null) => void;
  disabled: boolean;
}) => {
  const allInPriceValue = useMemo(() => {
    return getAllInPriceFromListPrice(value, null, sellerFee);
  }, [value, sellerFee]);

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const v = parseFloat(e.target.value);
      if (v >= 0 && v < Number.MAX_VALUE) {
        const netProceedsValueNew = getListPriceFromAllinPrice(
          v,
          null,
          sellerFee
        )!;
        setValue(netProceedsValueNew);
      } else {
        setValue(null);
      }
    },
    [setValue, sellerFee]
  );

  return (
    <PosCurrencyField
      rootProps={{ className: styles.floorCeilingPosCurrencyFieldRoot }}
      className={styles.textAlignRight}
      prefixDisplay={
        <div className={styles.floorCeilingInputPrefixContainer}>
          <div className={styles.floorCeilingIconContainer}>{icon}</div>
          <div className={styles.setPriceText}>
            <Content id={labelContentId} />
          </div>
          <div className={styles.setPriceText}>
            <span>{uiCurrency.sym}</span>
          </div>
        </div>
      }
      value={allInPriceValue ?? ''}
      onChange={onChange}
      uiCurrency={uiCurrency}
      disabled={disabled}
    />
  );
};
